import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react'
import { startTimer, stopTimer } from '$utils'

const RefetchTimerContext = createContext()

export const RefetchTimerProvider = ({ children }) => {
  const [isRunning, setIsRunning] = useState(false)

  // track interval and timeout IDs with refs
  const intervalIdRef = useRef(null)
  const timeoutIdRef = useRef(null)

  const startRefetch = useCallback((interval, callback, delay = 0) => {
    // clear any previous pending timeout
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current)
      timeoutIdRef.current = null
    }

    // Helper that actually launches the interval
    const launchInterval = () => {
      if (intervalIdRef.current) {
        console.info(
          `RefetchTimerProvider: Stopping previous timer ID ${intervalIdRef.current} before starting new one.`,
        )
        stopTimer(intervalIdRef.current)
      }
      intervalIdRef.current = startTimer(interval, callback)
      console.info(
        `RefetchTimerProvider: Started new timer ID ${intervalIdRef.current} with interval ${interval}ms.`,
      )
      setIsRunning(true)
    }

    if (delay > 0) {
      console.info(
        `RefetchTimerProvider: Delaying start of new timer by ${delay}ms.`,
      )
      timeoutIdRef.current = setTimeout(launchInterval, delay)
    } else {
      launchInterval()
    }
  }, [])

  const stopRefetch = useCallback(() => {
    // Clear pending timeout if any
    if (timeoutIdRef.current) {
      console.info(
        `RefetchTimerProvider: Clearing delayed start (timeout ID ${timeoutIdRef.current}).`,
      )
      clearTimeout(timeoutIdRef.current)
      timeoutIdRef.current = null
    }

    // Stop running interval if any
    if (intervalIdRef.current) {
      console.warn(
        `RefetchTimerProvider: Stopping timer ID ${intervalIdRef.current}.`,
      )
      stopTimer(intervalIdRef.current)
      intervalIdRef.current = null
    }

    // Update state
    setIsRunning(false)
  }, [])

  useEffect(() => {
    return () => {
      // Make sure we don't leave any timers running
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current)
      }
      if (intervalIdRef.current) {
        console.info(
          `RefetchTimerProvider: Cleaning up timer ID ${intervalIdRef.current} on unmount.`,
        )
        stopTimer(intervalIdRef.current)
      }
    }
  }, [])

  return (
    <RefetchTimerContext.Provider
      value={{ isRunning, startRefetch, stopRefetch }}
    >
      {children}
    </RefetchTimerContext.Provider>
  )
}

export const useRefetchTimer = () => useContext(RefetchTimerContext)
